/**
 * This module defines custom PropTypes shared within the application.
 *
 * To learn about validating React component props with PropTypes, see:
 *
 *     https://facebook.github.io/react/docs/typechecking-with-proptypes.html
 *
 * For component specific PropTypes, it's perfectly ok to inline them
 * to the component itself. If the type is shared or external (SDK or
 * API), however, it should be in this file for sharing with other
 * components.
 *
 * PropTypes should usually be validated only at the lowest level
 * where the props are used, not along the way in parents that pass
 * along the props to their children. Those parents should usually
 * just validate the presense of the prop key and that the value is
 * defined. This way we get the validation errors only in the most
 * specific place and avoid duplicate errros.
 */
import {
  arrayOf,
  bool,
  func,
  instanceOf,
  node,
  number,
  object,
  objectOf,
  oneOf,
  oneOfType,
  shape,
  string,
} from 'prop-types';
import Decimal from 'decimal.js';
import { types as sdkTypes } from './sdkLoader';
import {
  getAllTransitionsForEveryProcess,
  TX_TRANSITION_ACTORS,
} from '../transactions/transaction';
// NOTE: This file imports ../transactions/transaction.js, which may lead to circular dependency

const { UUID, LatLng, LatLngBounds, Money } = sdkTypes;
const TRANSITIONS = getAllTransitionsForEveryProcess();

// /// Types for listing types
export const STAFF_LISTING_TYPE = 'staff';
export const BUSINESS_PROFILE_LISTING_TYPE = 'business-profile';
export const DEAL_LISTING_TYPE = 'deal';
export const PRODUCT_LISTING_TYPE = 'product';
export const SERVICE_LISTING_TYPE = 'service';

// Supported schema types for custom fields added to extended data through configuration.
export const SCHEMA_TYPE_ENUM = 'enum';
export const SCHEMA_TYPE_MULTI_ENUM = 'multi-enum';
export const SCHEMA_TYPE_TEXT = 'text';
export const SCHEMA_TYPE_LONG = 'long';
export const SCHEMA_TYPE_BOOLEAN = 'boolean';
export const SCHEMA_TYPE_DATE = 'date';
export const SCHEMA_TYPE_PHONE = 'phone_number';
export const SCHEMA_TYPE_EMAIL = 'email';
export const SCHEMA_TYPE_SINGLE_IMAGE_UPLOAD = 'single_image_upload';
export const SCHEMA_TYPE_DOB = 'dob';

export const EXTENDED_DATA_SCHEMA_TYPES = [
  SCHEMA_TYPE_ENUM,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  SCHEMA_TYPE_LONG,
  SCHEMA_TYPE_BOOLEAN,
  SCHEMA_TYPE_DATE,
  SCHEMA_TYPE_PHONE,
  SCHEMA_TYPE_EMAIL,
  SCHEMA_TYPE_SINGLE_IMAGE_UPLOAD,
  SCHEMA_TYPE_DOB
];

export const LANGUAGES = {
  English: {
    name: "English",
    code: "en"
  },
  Arabic: {
    name: "Arabic",
    code: "ar"
  }
}

const propTypes = {};

// Fixed value
propTypes.value = val => oneOf([val]);

// SDK type instances
propTypes.uuid = instanceOf(UUID);
propTypes.latlng = instanceOf(LatLng);
propTypes.latlngBounds = instanceOf(LatLngBounds);
propTypes.money = instanceOf(Money);

// Configuration for currency formatting
propTypes.currencyConfig = shape({
  style: string.isRequired,
  currency: string.isRequired,
  currencyDisplay: string,
  useGrouping: bool,
  minimumFractionDigits: number,
  maximumFractionDigits: number,
});

// Configuration for a single route
propTypes.route = shape({
  name: string.isRequired,
  path: string.isRequired,
  exact: bool,
  strict: bool,
  component: oneOfType([object, func]).isRequired,
  loadData: func,
});

// Place object from LocationAutocompleteInput
propTypes.place = shape({
  address: string.isRequired,
  origin: propTypes.latlng,
  bounds: propTypes.latlngBounds, // optional viewport bounds
});

// Denormalised image object
propTypes.image = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('image').isRequired,
  attributes: shape({
    variants: objectOf(
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
  }),
});

// ImageAsset type from Asset Delivery API
propTypes.imageAsset = shape({
  id: string.isRequired,
  type: propTypes.value('imageAsset').isRequired,
  attributes: shape({
    variants: objectOf(
      shape({
        width: number.isRequired,
        height: number.isRequired,
        url: string.isRequired,
      })
    ),
  }),
});

// Denormalised user object
propTypes.currentUser = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('currentUser').isRequired,
  attributes: shape({
    banned: bool.isRequired,
    email: string.isRequired,
    emailVerified: bool.isRequired,
    profile: shape({
      firstName: string.isRequired,
      lastName: string.isRequired,
      displayName: string.isRequired,
      abbreviatedName: string.isRequired,
      bio: string,
    }).isRequired,
    stripeConnected: bool,
  }),
  profileImage: propTypes.image,
});

const userAttributes = shape({
  banned: propTypes.value(false).isRequired,
  deleted: propTypes.value(false).isRequired,
  profile: shape({
    displayName: string.isRequired,
    abbreviatedName: string.isRequired,
    bio: string,
  }),
});

// Listing queries can include author.
// Banned and deleted are not relevant then
// since banned and deleted users can't have listings.
const authorAttributes = shape({
  profile: shape({
    displayName: string.isRequired,
    abbreviatedName: string.isRequired,
    bio: string,
  }),
});

const deletedUserAttributes = shape({
  deleted: propTypes.value(true).isRequired,
});

const bannedUserAttributes = shape({
  banned: propTypes.value(true).isRequired,
});

// Denormalised user object
propTypes.user = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('user').isRequired,
  attributes: oneOfType([
    userAttributes,
    authorAttributes,
    deletedUserAttributes,
    bannedUserAttributes,
  ]).isRequired,
  profileImage: propTypes.image,
});

export const LISTING_STATE_DRAFT = 'draft';
export const LISTING_STATE_PENDING_APPROVAL = 'pendingApproval';
export const LISTING_STATE_PUBLISHED = 'published';
export const LISTING_STATE_CLOSED = 'closed';

const LISTING_STATES = [
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
  LISTING_STATE_CLOSED,
];

const listingAttributes = shape({
  title: string.isRequired,
  description: string,
  geolocation: propTypes.latlng,
  deleted: propTypes.value(false),
  state: oneOf(LISTING_STATES),
  price: propTypes.money,
  publicData: object,
});

const AVAILABILITY_PLAN_DAY = 'availability-plan/day';
const AVAILABILITY_PLAN_TIME = 'availability-plan/time';
export const DAYS_OF_WEEK = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const availabilityPlan = shape({
  type: oneOf([AVAILABILITY_PLAN_DAY, AVAILABILITY_PLAN_TIME]).isRequired,
  timezone: string,
  entries: arrayOf(
    shape({
      dayOfWeek: oneOf(DAYS_OF_WEEK).isRequired,
      seats: number.isRequired,
      start: string,
      end: string,
    })
  ),
});

propTypes.availabilityPlan = availabilityPlan;

const ownListingAttributes = shape({
  title: string.isRequired,
  description: string,
  geolocation: propTypes.latlng,
  deleted: propTypes.value(false).isRequired,
  state: oneOf(LISTING_STATES).isRequired,
  price: propTypes.money,
  availabilityPlan: availabilityPlan,
  publicData: object.isRequired,
});

const deletedListingAttributes = shape({
  deleted: propTypes.value(true).isRequired,
});

// Denormalised listing object
propTypes.listing = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('listing').isRequired,
  attributes: oneOfType([listingAttributes, deletedListingAttributes]).isRequired,
  author: propTypes.user,
  images: arrayOf(propTypes.image),
});

// Denormalised ownListing object
propTypes.ownListing = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('ownListing').isRequired,
  attributes: oneOfType([ownListingAttributes, deletedListingAttributes]).isRequired,
  author: propTypes.currentUser,
  images: arrayOf(propTypes.image),
});

export const BOOKING_STATE_PENDING = 'pending';
export const BOOKING_STATE_ACCEPTED = 'accepted';
export const BOOKING_STATE_DECLINED = 'declined';
export const BOOKING_STATE_CANCELLED = 'cancelled';
export const BOOKING_STATES = [
  BOOKING_STATE_PENDING,
  BOOKING_STATE_ACCEPTED,
  BOOKING_STATE_DECLINED,
  BOOKING_STATE_CANCELLED,
];

// Denormalised booking object
propTypes.booking = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('booking').isRequired,
  attributes: shape({
    end: instanceOf(Date).isRequired,
    start: instanceOf(Date).isRequired,
    displayStart: instanceOf(Date),
    displayEnd: instanceOf(Date),
    state: oneOf(BOOKING_STATES),
  }),
});

// A time slot that covers one day, having a start and end date.
export const TIME_SLOT_TIME = 'time-slot/time';

// Denormalised time slot object
propTypes.timeSlot = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('timeSlot').isRequired,
  attributes: shape({
    type: oneOf([TIME_SLOT_TIME]).isRequired,
    end: instanceOf(Date).isRequired,
    start: instanceOf(Date).isRequired,
  }),
});

// Denormalised availability exception object
propTypes.availabilityException = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('availabilityException').isRequired,
  attributes: shape({
    end: instanceOf(Date).isRequired,
    seats: number.isRequired,
    start: instanceOf(Date).isRequired,
  }),
});

export const STOCK_ONE_ITEM = 'oneItem';
export const STOCK_MULTIPLE_ITEMS = 'multipleItems';
export const STOCK_INFINITE_ONE_ITEM = 'infiniteOneItem';
export const STOCK_INFINITE_MULTIPLE_ITEMS = 'infiniteMultipleItems';
export const STOCK_INFINITE_ITEMS = [STOCK_INFINITE_ONE_ITEM, STOCK_INFINITE_MULTIPLE_ITEMS];
export const STOCK_TYPES = [
  STOCK_ONE_ITEM,
  STOCK_MULTIPLE_ITEMS,
  STOCK_INFINITE_ONE_ITEM,
  STOCK_INFINITE_MULTIPLE_ITEMS,
];

propTypes.transition = shape({
  createdAt: instanceOf(Date).isRequired,
  by: oneOf(TX_TRANSITION_ACTORS).isRequired,
  transition: oneOf(TRANSITIONS).isRequired,
});

// Possible amount of stars in a review
export const REVIEW_RATINGS = [1, 2, 3, 4, 5];

// Review types: review of a provider and of a customer
export const REVIEW_TYPE_OF_PROVIDER = 'ofProvider';
export const REVIEW_TYPE_OF_CUSTOMER = 'ofCustomer';

// A review on a user
propTypes.review = shape({
  id: propTypes.uuid.isRequired,
  attributes: shape({
    createdAt: instanceOf(Date).isRequired,
    content: string,
    rating: oneOf(REVIEW_RATINGS),
    state: string.isRequired,
    type: oneOf([REVIEW_TYPE_OF_PROVIDER, REVIEW_TYPE_OF_CUSTOMER]).isRequired,
  }).isRequired,
  author: propTypes.user,
  subject: propTypes.user,
});

// A Stripe account entity
propTypes.stripeAccount = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('stripeAccount').isRequired,
  attributes: shape({
    stripeAccountId: string.isRequired,
    stripeAccountData: object,
  }),
});

propTypes.defaultPaymentMethod = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('stripePaymentMethod').isRequired,
  attributes: shape({
    type: propTypes.value('stripe-payment-method/card').isRequired,
    stripePaymentMethodId: string.isRequired,
    card: shape({
      brand: string.isRequired,
      expirationMonth: number.isRequired,
      expirationYear: number.isRequired,
      last4Digits: string.isRequired,
    }).isRequired,
  }),
});

export const LINE_ITEM_NIGHT = 'line-item/night';
export const LINE_ITEM_DAY = 'line-item/day';
export const LINE_ITEM_HOUR = 'line-item/hour';
export const LINE_ITEM_ITEM = 'line-item/item';
export const LINE_ITEM_CUSTOMER_COMMISSION = 'line-item/customer-commission';
export const LINE_ITEM_PROVIDER_COMMISSION = 'line-item/provider-commission';
export const LINE_ITEM_SHIPPING_FEE = 'line-item/shipping-fee';
export const LINE_ITEM_PICKUP_FEE = 'line-item/pickup-fee';
export const LINE_ITEM_VAT_TAX = 'line-item/vat-tax';
export const LINE_ITEM_VAT_TAX_ON_NOW_FEE = 'line-item/vat-tax-on-now-fee';
export const LINE_ITEM_DEAL_DISCOUNT = 'line-item/deal-discount';
export const LINE_ITEM_TRAVEL = 'line-item/travel';
// Platform-funded discount code: customer-only, applied last (after VAT) --
// see server/api-util/couponPricing.js.
export const LINE_ITEM_DISCOUNT_CODE = 'line-item/discount-code';
// Tap's card processing fee: a provider-only deduction in the merchant's
// payout, negative, added at booking time -- see server/api-util/tapFeeLineItem.js.
export const LINE_ITEM_TAP_PROCESSING_FEE = 'line-item/tap-processing-fee';

// Templates live in the OneSignal app of whoever RECEIVES the push: the
// merchant map below is delivered through the merchant app, the customer map
// through the customer app (see server/api/onesignalNotifications.js, which
// picks the app id from the sender's role). Names are unique across both maps
// because the server flattens them into one lookup for the in-app feed's
// notificationType. Keep in sync with Now-Customer/src/appTypes/enums/
// onesignalTemplets.ts and Now-Merchant/src/appTypes/enums/oneSignalTemplets.ts.
export const oneSignalMerchantNotificationTemplets = {
  LIKE: '9cccef49-c0f5-4d9e-bffe-5e36513c340d',
  ORDER_PLACED: '5dd75b78-ab29-4f37-8b33-83936dd95301',
  RAISE_DISPUTE: '9bdf7ae1-eb50-4337-af8b-b384116a22d0',
  RESCHEDULE_REQUEST: '65335055-687e-473a-9cb4-990b1da9a0cf',
  // Self-serve reschedule/cancellation, informational only.
  CUSTOMER_RESCHEDULED: '71c986af-6a55-44b0-94e3-93c2d83135b2',
  CUSTOMER_CANCELLED: '9ac121c9-a3ff-4ae8-aa3d-09f4ab28f889',
  // Negotiated model — sent to the merchant when the customer acts.
  NEGOTIATION_QUOTE_REQUESTED: '1c424106-b925-4d97-95ca-0473179f5204',
  NEGOTIATION_COUNTER_OFFER: '16b14e78-71e8-45cb-9e85-5c46be6c345f',
  NEGOTIATION_OFFER_ACCEPTED: 'c0352511-72ac-4400-80aa-f71880a7c75a',
  NEGOTIATION_OFFER_DECLINED: '316bed12-4aa4-4e2d-a487-946c0885bc5d',
  NEGOTIATION_NEW_MESSAGE: '2e998ce9-bd28-4e87-b8d2-a27bb8eea63d',
}
export const oneSignalCustomerNotificationTemplets = {
  ACCEPT_DISPUTE: '1fdd9376-8a56-4a5f-b4b5-35bac0bf349f',
  BOOKING_COMPLETED: '3e64d29c-893e-42b0-b042-b8e543b9690a',
  BUZZ: '2178e33c-c468-4233-9b48-1511ce46c5de',
  DECLINE_DISPUTE: '4e1d958a-7931-47c1-a184-32b299d27936',
  ORDER_DELIVERED: '687c4084-f741-47dc-b4e4-649f100ee6e0',
  RESCHEDULE_APPROVED: 'd71240fd-e74a-4673-97c5-375c555771a6',
  RESCHEDULE_DENIED: '1d343637-e2bb-41a7-8c6e-a7dbc4194dad',
  // Negotiated model — sent to the customer when the merchant acts.
  NEGOTIATION_OFFER_RECEIVED: '6717e416-1f8f-40e1-afdd-85315ae46db0',
  NEGOTIATION_COUNTER_ACCEPTED: 'aa82da19-3711-4a1c-9292-bb180b3d74e9',
  // Merchant rejected the customer's counter; the standing offer returns.
  NEGOTIATION_COUNTER_DECLINED: 'aaed79c1-0efc-47ad-9c93-604a95d7afa9',
  // Merchant pulled their own offer — ends the negotiation.
  NEGOTIATION_OFFER_WITHDRAWN: 'e3c09f33-b046-4d5c-9c86-b9ac7eb0a852',
  NEGOTIATION_MESSAGE_RECEIVED: '421ac80f-6241-4ad8-bc5d-8e1429d6b719',
}


export const LINE_ITEMS = [
  LINE_ITEM_NIGHT,
  LINE_ITEM_DAY,
  LINE_ITEM_HOUR,
  LINE_ITEM_ITEM,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
  LINE_ITEM_SHIPPING_FEE,
  LINE_ITEM_PICKUP_FEE,
  LINE_ITEM_VAT_TAX_ON_NOW_FEE,
  LINE_ITEM_DISCOUNT_CODE,
  LINE_ITEM_TAP_PROCESSING_FEE,
];
export const LISTING_UNIT_TYPES = [LINE_ITEM_NIGHT, LINE_ITEM_DAY, LINE_ITEM_HOUR, LINE_ITEM_ITEM, LINE_ITEM_SHIPPING_FEE, LINE_ITEM_PICKUP_FEE];

propTypes.lineItemUnitType = oneOf(LISTING_UNIT_TYPES);

const requiredLineItemPropType = (props, propName, componentName) => {
  const prop = props[propName];

  if (!prop || prop === '') {
    return new Error(`Missing line item code prop from ${componentName}.`);
  }
  if (!/^line-item\/.+/.test(prop)) {
    return new Error(`Invalid line item code value ${prop} passed to ${componentName}.`);
  }
};

propTypes.lineItems = arrayOf(
  shape({
    code: requiredLineItemPropType,
    includeFor: arrayOf(oneOf(['customer', 'provider'])).isRequired,
    quantity: instanceOf(Decimal),
    unitPrice: propTypes.money.isRequired,
    lineTotal: propTypes.money.isRequired,
    reversal: bool.isRequired,
  })
);

// Denormalised transaction object
propTypes.transaction = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('transaction').isRequired,
  attributes: shape({
    createdAt: instanceOf(Date),
    processName: string.isRequired,
    lastTransitionedAt: instanceOf(Date).isRequired,
    lastTransition: oneOf(TRANSITIONS).isRequired,

    // An inquiry won't need a total sum nor a booking so these are
    // optional.
    payinTotal: propTypes.money,
    payoutTotal: propTypes.money,

    lineItems: propTypes.lineItems,
    transitions: arrayOf(propTypes.transition).isRequired,
  }),
  booking: propTypes.booking,
  listing: propTypes.listing,
  customer: propTypes.user,
  provider: propTypes.user,
  reviews: arrayOf(propTypes.review),
});

// Denormalised transaction message
propTypes.message = shape({
  id: propTypes.uuid.isRequired,
  type: propTypes.value('message').isRequired,
  attributes: shape({
    createdAt: instanceOf(Date).isRequired,
    content: string.isRequired,
  }).isRequired,
  sender: propTypes.user,
});

// Pagination information in the response meta
propTypes.pagination = shape({
  page: number.isRequired,
  perPage: number.isRequired,
  totalItems: number,
  totalPages: number,
});

// Search filter definition
propTypes.filterConfig = arrayOf(
  shape({
    id: string.isRequired,
    label: node,
    type: string.isRequired,
    group: oneOf(['primary', 'secondary']).isRequired,
    queryParamNames: arrayOf(string).isRequired,
    config: object,
  }).isRequired
);

// Default search filters definition
propTypes.defaultFiltersConfig = arrayOf(
  shape({
    key: string.isRequired,
    schemaType: oneOf(['price', 'text', 'dates']).isRequired,
    min: number,
    max: number,
    step: number,
  }).isRequired
);

// Extended data config
propTypes.userType = shape({
  userType: string.isRequired,
  label: string.isRequired,
  defaultUserFields: shape({
    displayName: bool,
    phoneNumber: bool,
  }),
  displayNameSettings: shape({
    displayInSignUp: bool,
    required: bool,
  }),
  phoneNumberSettings: shape({
    displayInSignUp: bool,
    required: bool,
  }),
});
propTypes.userTypes = arrayOf(propTypes.userType);

propTypes.fieldEnumOptions = arrayOf(
  shape({
    option: oneOfType([string, number]).isRequired,
    label: string.isRequired,
  })
);

propTypes.userField = shape({
  key: string.isRequired,
  scope: string,
  schemaType: oneOf(EXTENDED_DATA_SCHEMA_TYPES).isRequired,
  enumOptions: propTypes.fieldEnumOptions,
  showConfig: shape({
    label: string.isRequired,
    displayInProfile: bool,
  }),
  saveConfig: shape({
    label: string.isRequired,
    placeholderMessage: string,
    isRequired: bool,
    requiredMessage: string,
    displayInSignUp: bool,
  }).isRequired,
  userTypeConfig: shape({
    limitToUserTypeIds: bool.isRequired,
    userTypeIds: arrayOf(string),
  }),
});
propTypes.userFields = arrayOf(propTypes.userField);

propTypes.listingType = shape({
  listingType: string.isRequired,
  label: string.isRequired,
  transactionType: shape({
    process: string.isRequired,
    alias: string.isRequired,
    unitType: string.isRequired,
  }).isRequired,
  defaultListingFields: shape({
    price: bool,
    location: bool,
    payoutDetails: bool,
    shipping: bool,
    pickup: bool,
  }),
});
propTypes.listingTypes = arrayOf(propTypes.userType);

propTypes.listingField = shape({
  key: string.isRequired,
  scope: string,
  schemaType: oneOf(EXTENDED_DATA_SCHEMA_TYPES).isRequired,
  enumOptions: propTypes.fieldEnumOptions,
  filterConfig: shape({
    indexForSearch: bool,
    label: string.isRequired,
    group: oneOf(['primary', 'secondary']),
    filterType: string,
  }),
  showConfig: shape({
    label: string.isRequired,
    isDetail: bool,
  }),
  saveConfig: shape({
    label: string.isRequired,
    placeholderMessage: string,
    isRequired: bool,
    requiredMessage: string,
  }).isRequired,
  listingTypeConfig: shape({
    limitToListingTypeIds: bool.isRequired,
    listingTypeIds: arrayOf(string),
  }),
  categoryConfig: shape({
    limitToCategoryIds: bool.isRequired,
    categoryIds: arrayOf(string),
  }),
});

propTypes.listingFields = arrayOf(propTypes.listingField);

const sortConfigOptionWithLabel = shape({
  key: oneOf(['createdAt', '-createdAt', 'price', '-price', 'relevance']).isRequired,
  label: string.isRequired,
  longLabel: string,
});

const sortConfigOptionWithTranslationKey = shape({
  key: oneOf(['createdAt', '-createdAt', 'price', '-price', 'relevance']).isRequired,
  labelTranslationKey: string.isRequired,
  labelTranslationKeyLong: string,
});

propTypes.sortConfig = shape({
  active: bool,
  queryParamName: oneOf(['sort']).isRequired,
  relevanceKey: string.isRequired,
  conflictingFilters: arrayOf(string),
  options: arrayOf(oneOfType([sortConfigOptionWithLabel, sortConfigOptionWithTranslationKey])),
});

export const ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND = 'transaction-listing-not-found';
export const ERROR_CODE_TRANSACTION_INVALID_TRANSITION = 'transaction-invalid-transition';
export const ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER =
  'transaction-already-reviewed-by-customer';
export const ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER =
  'transaction-already-reviewed-by-provider';
export const ERROR_CODE_TRANSACTION_BOOKING_TIME_NOT_AVAILABLE =
  'transaction-booking-time-not-available';
export const ERROR_CODE_TRANSACTION_LISTING_INSUFFICIENT_STOCK =
  'transaction-listing-insufficient-stock';
export const ERROR_CODE_PAYMENT_FAILED = 'transaction-payment-failed';
export const ERROR_CODE_CHARGE_ZERO_PAYIN = 'transaction-charge-zero-payin';
export const ERROR_CODE_CHARGE_ZERO_PAYOUT = 'transaction-charge-zero-payout';
export const ERROR_CODE_EMAIL_TAKEN = 'email-taken';
export const ERROR_CODE_PHONE_TAKEN = 'phone-taken';
export const ERROR_CODE_DELETED_USER = 'deleted-user';
export const ERROR_CODE_EMAIL_NOT_FOUND = 'email-not-found';
export const ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS = 'email-too-many-verification-requests';
export const ERROR_CODE_UPLOAD_OVER_LIMIT = 'request-upload-over-limit';
export const ERROR_CODE_VALIDATION_INVALID_PARAMS = 'validation-invalid-params';
export const ERROR_CODE_VALIDATION_INVALID_VALUE = 'validation-invalid-value';
export const ERROR_CODE_NOT_FOUND = 'not-found';
export const ERROR_CODE_FORBIDDEN = 'forbidden';
export const ERROR_CODE_MISSING_STRIPE_ACCOUNT = 'transaction-missing-stripe-account';
export const ERROR_CODE_STOCK_OLD_TOTAL_MISMATCH = 'old-total-mismatch';

const ERROR_CODES = [
  ERROR_CODE_TRANSACTION_LISTING_NOT_FOUND,
  ERROR_CODE_TRANSACTION_INVALID_TRANSITION,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_CUSTOMER,
  ERROR_CODE_TRANSACTION_ALREADY_REVIEWED_BY_PROVIDER,
  ERROR_CODE_PAYMENT_FAILED,
  ERROR_CODE_CHARGE_ZERO_PAYIN,
  ERROR_CODE_CHARGE_ZERO_PAYOUT,
  ERROR_CODE_EMAIL_TAKEN,
  ERROR_CODE_EMAIL_NOT_FOUND,
  ERROR_CODE_TOO_MANY_VERIFICATION_REQUESTS,
  ERROR_CODE_UPLOAD_OVER_LIMIT,
  ERROR_CODE_VALIDATION_INVALID_PARAMS,
  ERROR_CODE_VALIDATION_INVALID_VALUE,
  ERROR_CODE_NOT_FOUND,
  ERROR_CODE_FORBIDDEN,
  ERROR_CODE_MISSING_STRIPE_ACCOUNT,
  ERROR_CODE_STOCK_OLD_TOTAL_MISMATCH,
];

// Deal Types
export const DealType = Object.freeze({
  AMOUNT_DISCOUNT: 'amount-discount',
  PERCENTAGE_DISCOUNT: 'percentage-discount',
  BULK_DISCOUNT: 'bulk-discount',
  PRODUCT_BUNDLES: 'product-bundles',
  SERVICE_BUNDLES: 'service-bundles',
  FLASH_SALES: 'flash-sales',
  LIMITED_TIME_OFFERS: 'limited-time-offers',
  REFERRAL_DISCOUNTS: 'referral-discounts',
  FRIEND_DISCOUNTS: 'friend-discounts',
  GROUP_DISCOUNTS: 'group-discounts',
  VIRAL_DISCOUNTS: 'viral-discounts',
  CASHBACK_DISCOUNTS: 'cashback-discounts',
  PROMO_CODES: 'promo-codes',
  NEXT_VISIT_DISCOUNT: 'next-visit-discount',
  BUY_ONE_GET_ONE_FREE: 'buy-one-get-one-free',
  BUY_ONE_GET_ONE_HALF_OFF: 'buy-one-get-one-half-off',
  FREE_GIFTS_WITH_PURCHASE: 'free-gifts-with-purchase',
  FREE_SAMPLES: 'free-samples',
  FREE_DELIVERIES: 'free-deliveries',
});
export const discountedPrice = [
  DealType.AMOUNT_DISCOUNT,
  DealType.PERCENTAGE_DISCOUNT,
  DealType.BULK_DISCOUNT,
  DealType.BUY_ONE_GET_ONE_FREE,
  DealType.BUY_ONE_GET_ONE_HALF_OFF,
  DealType.PRODUCT_BUNDLES,
  DealType.SERVICE_BUNDLES,
  DealType.PROMO_CODES,
  DealType.VIRAL_DISCOUNTS,
]
export const dealTypesWithDiscountFields = [DealType.AMOUNT_DISCOUNT, DealType.PERCENTAGE_DISCOUNT];
export const dealTypesWithMultiItem = [DealType.BULK_DISCOUNT, DealType.PRODUCT_BUNDLES, DealType.SERVICE_BUNDLES, DealType.PROMO_CODES, DealType.VIRAL_DISCOUNTS, DealType.FLASH_SALES]
export const dealTypesWithSelectFields = [
  DealType.BULK_DISCOUNT,
  DealType.PRODUCT_BUNDLES,
  DealType.SERVICE_BUNDLES,
  DealType.FLASH_SALES,
  DealType.LIMITED_TIME_OFFERS,
  DealType.REFERRAL_DISCOUNTS,
  DealType.FRIEND_DISCOUNTS,
  DealType.GROUP_DISCOUNTS,
  DealType.VIRAL_DISCOUNTS,
  DealType.CASHBACK_DISCOUNTS,
  DealType.PROMO_CODES,
  DealType.NEXT_VISIT_DISCOUNT,
];

export const specialDealTypes = [
  DealType.BUY_ONE_GET_ONE_FREE,
  DealType.BUY_ONE_GET_ONE_HALF_OFF,
  DealType.FREE_GIFTS_WITH_PURCHASE,
];
export const bogoDealTypes = [
  DealType.BUY_ONE_GET_ONE_FREE,
  DealType.BUY_ONE_GET_ONE_HALF_OFF,
]
export const bundlesDealTypes = [DealType.SERVICE_BUNDLES, DealType.PRODUCT_BUNDLES];

export const timeBasedDealTypes = [
  DealType.NEXT_VISIT_DISCOUNT,
  DealType.FREE_SAMPLES,
];
export const dealTypesDateTime = [
  DealType.FLASH_SALES,
  DealType.LIMITED_TIME_OFFERS,
  DealType.NEXT_VISIT_DISCOUNT,
]

export const requirementDealTypes = [
  DealType.REFERRAL_DISCOUNTS,
  DealType.FRIEND_DISCOUNTS,
  DealType.CASHBACK_DISCOUNTS,
  DealType.FREE_DELIVERIES,
  DealType.GROUP_DISCOUNTS,
  DealType.VIRAL_DISCOUNTS,
];
export const groupDiscount = [DealType.GROUP_DISCOUNTS, DealType.VIRAL_DISCOUNTS];

// API error
propTypes.apiError = shape({
  id: propTypes.uuid.isRequired,
  status: number.isRequired,
  code: oneOf(ERROR_CODES).isRequired,
  title: string.isRequired,
  meta: object,
});

propTypes.assetDeliveryApiError = shape({
  code: oneOf(ERROR_CODES).isRequired,
  id: string.isRequired,
  status: number.isRequired,
  title: string.isRequired,
});

// Storable error prop type. (Error object should not be stored as it is.)
propTypes.error = shape({
  type: propTypes.value('error').isRequired,
  name: string.isRequired,
  message: string,
  status: number,
  statusText: string,
  apiErrors: arrayOf(oneOfType([propTypes.apiError, propTypes.assetDeliveryApiError])),
});

// Options for showing just date or date and time on TimeRange and OrderBreakdown
export const DATE_TYPE_DATE = 'date';
export const DATE_TYPE_TIME = 'time';
export const DATE_TYPE_DATETIME = 'datetime';

propTypes.dateType = oneOf([DATE_TYPE_DATE, DATE_TYPE_TIME, DATE_TYPE_DATETIME]);

export { propTypes };
export const listingType = [
  { key: 'deal', label: 'Deal' },
  { key: 'service', label: 'Service' },
  { key: 'product', label: 'Product' },
  { key: 'business-profile', label: 'Business' },
];

export const ListingTypes = {
  DEAL: 'deal',
  BUSINESS_PROFILE: 'business-profile',
  PRODUCT: 'product',
  SERVICE: 'service',
  DAILY_BOOKING: 'daily-booking',
  BUSINESS_SERVICE: 'businessProfile',
  PERCENTAGE_DISCOUNT: 'percentage-discount',
  AMOUNT_DISCOUNT: 'amount-discount',
  BULK_DISCOUNT: 'bulk-discount',
  BUY_ONE_GET_ONE_FREE: 'buy-one-get-one-free',
  BUY_ONE_GET_ONE_HALF_OFF: 'buy-one-get-one-half-off',
  PRODUCT_BUNDLE: 'product-bundle',
  SERVICE_BUNDLE: 'service-bundle',
  PROMO_CODE: 'promo-code',
  VIRAL_DISCOUNT: 'viral-discount',
  FLASH_SALE: 'flash-sale',
  LIMITED_TIME_OFFER: 'limited-time-offer',
  FREE_SAMPLE: 'free-sample',
  STAFF: 'staff',
  FREE_GIFTS_WITH_PURCHASE: 'free-gifts-with-purchase',
  REFERRAL_DISCOUNT: 'referral-discount',
  FRIEND_DISCOUNT: 'friend-discount',
  GROUP_BUYING: 'group-buying',
  CASH_BACK: 'cash-back',
  FREE_DELIVERY: 'free-delivery',
  NEXT_VISIT_DISCOUNT: 'next-visit-discount',
}
